import { render } from "preact";
import { type Signal, signal } from "@preact/signals";
import styles from "./ask.module.css";

/** Questions for the player, over everything else, asked by the game. */
export interface Asker {
  /** Asks `question`: resolves true if the player goes ahead (by the button `yes`). */
  ask(question: string, yes: string, no?: string): Promise<boolean>;
  /** The way back, as やめる goes (null while nothing is asked). */
  readonly wayBack: (() => void) | null;
}

interface Asked {
  question: string;
  /** The button that goes ahead (the other one, やめる, does not). */
  yes: string;
  no: string;
  answer(go: boolean): void;
}

/** Asks the player to choose before proceeding. */
export function mountAsk(root: HTMLElement): Asker {
  const asked = signal<Asked | null>(null);
  render(<AskCard asked={asked} />, root);
  return {
    ask: (question, yes, no = "やめる") =>
      new Promise((done) => {
        asked.value = {
          question,
          yes,
          no,
          answer(go) {
            asked.value = null;
            done(go);
          },
        };
      }),
    get wayBack() {
      const a = asked.value;
      return a && (() => a.answer(false));
    },
  };
}

function AskCard({ asked }: { asked: Signal<Asked | null> }) {
  const a = asked.value;
  if (!a) return null;
  return (
    <div class={styles.ask}>
      <div class={styles.card} role="alertdialog" aria-modal="true">
        <p class={styles.question}>{a.question}</p>
        <div class={`row ${styles.actions}`}>
          <button type="button" class="reset" onClick={() => a.answer(false)}>{a.no}</button>
          <button type="button" class={styles.yes} onClick={() => a.answer(true)}>
            {a.yes}
          </button>
        </div>
      </div>
    </div>
  );
}
